// public/js/modules/map/geocode.js
// Address lookup against the ArcGIS World GeocodeServer.
//
// Same key as the satellite base layer (window.APP_CONFIG.ESRI_API_KEY) and for
// the same reason: it is a browser key, restricted in the ArcGIS portal, so the
// request goes straight from the page. Routing a keystroke through our Lambda
// would only add a round trip to something the user is typing.
//
// `forStorage=false` on every call. That is the free, non-stored tier: show a
// result, forget it. WRITING a geocoded coordinate into a record is a stored
// geocode and billed differently - if we ever do that, it needs its own call
// with forStorage=true. See docs/maps.md.
const SUGGEST_URL = 'https://geocode-api.arcgis.com/arcgis/rest/services/World/GeocodeServer/suggest';
const FIND_URL = 'https://geocode-api.arcgis.com/arcgis/rest/services/World/GeocodeServer/findAddressCandidates';

const apiKey = () => (typeof window !== 'undefined' && window.APP_CONFIG && window.APP_CONFIG.ESRI_API_KEY) || '';

/** Without a key there is no address search at all - the caller hides the UI. */
export function geocodingAvailable() {
  return !!apiKey();
}

// Operations people paste coordinates far more often than they type a street,
// and a geocoder answers a coordinate pair with nonsense. Catch it first.
// Accepted: "47.37, 8.54", "47.37 8.54", "N 47.37 E 8.54", "47.37N 8.54E".
export function parseCoordinates(text) {
  const s = String(text || '').trim();
  if (!s) return null;
  const m = s.match(/^([NS])?\s*(-?\d{1,3}(?:[.,]\d+)?)\s*([NS])?\s*[,;\s]\s*([EW])?\s*(-?\d{1,3}(?:[.,]\d+)?)\s*([EW])?$/i);
  if (!m) return null;
  const num = (v) => parseFloat(String(v).replace(',', '.'));
  const sign = (a, b, neg) => ((a || b || '').toUpperCase() === neg ? -1 : 1);
  const lat = num(m[2]) * sign(m[1], m[3], 'S');
  const lon = num(m[5]) * sign(m[4], m[6], 'W');
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  return { lat, lon };
}

function failed(json, res) {
  // ArcGIS answers an expired or unprivileged key with HTTP 200 and an error
  // body, so the status code alone says nothing.
  const e = json && json.error;
  if (e) {
    if (e.code === 498 || e.code === 499) return 'The map key is not valid for address search.';
    if (e.code === 403) return 'The map key is not allowed to search addresses.';
    return e.message || 'Address search failed.';
  }
  if (!res.ok) return `Address search failed (${res.status}).`;
  return null;
}

/**
 * Type-ahead suggestions. `near` ([lon, lat], usually the map centre) pulls
 * results towards what the user is looking at, which is what makes a short
 * query like "hangar 3" useful at all.
 * → [{ text, magicKey }]
 */
export async function suggestPlaces(text, { near = null, signal = null } = {}) {
  const key = apiKey();
  if (!key || !text || text.trim().length < 3) return [];
  const params = new URLSearchParams({
    f: 'json', text: text.trim(), maxSuggestions: '8', token: key,
  });
  if (near && Number.isFinite(near[0]) && Number.isFinite(near[1])) {
    params.set('location', `${near[0]},${near[1]}`);
  }
  const res = await fetch(`${SUGGEST_URL}?${params}`, { signal });
  const json = await res.json().catch(() => null);
  const msg = failed(json, res);
  if (msg) throw new Error(msg);
  return (json.suggestions || [])
    .filter(s => !s.isCollection)   // a collection has no single location
    .map(s => ({ text: s.text, magicKey: s.magicKey }));
}

/**
 * Turn a suggestion (or a plain string) into a place with coordinates.
 * → { label, lon, lat, extent } | null
 */
export async function resolvePlace({ text, magicKey = null }, { near = null, signal = null } = {}) {
  const key = apiKey();
  if (!key || !text) return null;
  const params = new URLSearchParams({
    f: 'json', singleLine: text, outFields: 'Place_addr,PlaceName,Type',
    maxLocations: '1', outSR: '4326', forStorage: 'false', token: key,
  });
  if (magicKey) params.set('magicKey', magicKey);
  if (near && Number.isFinite(near[0]) && Number.isFinite(near[1])) {
    params.set('location', `${near[0]},${near[1]}`);
  }
  const res = await fetch(`${FIND_URL}?${params}`, { signal });
  const json = await res.json().catch(() => null);
  const msg = failed(json, res);
  if (msg) throw new Error(msg);
  const c = (json.candidates || [])[0];
  if (!c || !c.location) return null;
  return {
    label: c.address || text,
    lon: c.location.x,
    lat: c.location.y,
    // Present for anything bigger than a house - a city, an airport, a region.
    // Flying to its extent beats guessing a zoom level.
    extent: c.extent || null,
  };
}
